import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * How many dialogs are currently up. The background is only released when the
 * last one closes — a nested open/close pair must not un-inert the page while
 * an outer dialog is still modal.
 */
let openCount = 0;

function acquireBackground() {
  openCount += 1;
  if (openCount === 1) document.querySelector('.app')?.setAttribute('inert', '');
}

function releaseBackground() {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) document.querySelector('.app')?.removeAttribute('inert');
}

interface DialogProps {
  onClose: () => void;
  /** Accessible name for the dialog. */
  label: string;
  /** `drawer` is the right-hand slide-over; `modal` is the centred sheet. */
  variant: 'drawer' | 'modal';
  /**
   * While true, Escape and scrim-click are ignored. Both used to tear the
   * enrollment wizard down mid-submit, and the multi-account enrollment is not
   * atomic — half the accounts would already be enrolled with no way back to
   * the wizard that said so.
   */
  busy?: boolean;
  children: ReactNode;
}

/**
 * The one modal shell: portalled, focus-trapping, background-inerting.
 *
 * Both dialogs in the app used to render in place — the account drawer as the
 * last child of the panel it covered, the enrollment wizard as a sibling of the
 * workspace. Neither set `aria-modal`, trapped focus, restored focus or stopped
 * the page behind from scrolling, so Shift+Tab out of the drawer's Close button
 * walked backwards into the list underneath it.
 *
 * Portalling to `document.body` is what makes the rest possible: with the
 * dialog outside `.app`, `.app` itself can be marked `inert`, which removes the
 * background from the tab order AND from hit-testing in one attribute — so
 * background controls also stop producing tooltips, which they did even while
 * covered by the scrim.
 */
export function Dialog({ onClose, label, variant, busy = false, children }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  /** Where the pointer went DOWN — see the drag guard below. */
  const pressOrigin = useRef<EventTarget | null>(null);

  /*
   * Captured during RENDER, not in the mount effect.
   *
   * Parent effects run after child effects, so by the time this component's
   * effect fires, a child that autofocuses on mount — the enrollment wizard's
   * step-1 input does exactly this — has already moved focus inside the dialog.
   * Reading `activeElement` there recorded a node that is destroyed when the
   * dialog closes, so "restore focus" restored it to nothing and dropped the
   * user at the top of the document. Render runs before any of that.
   *
   * Assigning a ref during render is safe here because it is idempotent: under
   * StrictMode's double render the active element has not changed between the
   * two passes, and the guard keeps the first value regardless.
   */
  const returnTo = useRef<HTMLElement | null>(null);
  if (returnTo.current === null) {
    returnTo.current = document.activeElement as HTMLElement | null;
  }

  const requestClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    acquireBackground();

    return () => {
      releaseBackground();
      // Put the caret back on whatever opened this, so dismissing a drawer
      // returns a keyboard user to the row they opened it from.
      returnTo.current?.focus?.();
    };
  }, []);

  // Initial focus. The wizard already autofocuses its own first input, so only
  // move focus when nothing inside has claimed it.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (panel.contains(document.activeElement)) return;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel).focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel) return;

      if (e.key === 'Escape') {
        /*
         * `stopImmediatePropagation`: every Escape handler in this app is bound
         * to `document` in the capture phase, and `stopPropagation` does not
         * stop other listeners on the same node in the same phase — so it would
         * not have stopped the one thing it was written to stop, a second
         * dialog's handler underneath. tips.ts registers before React mounts
         * and so still gets first refusal, which is correct: a visible tooltip
         * is dismissed before the dialog behind it.
         */
        e.stopImmediatePropagation();
        requestClose();
        return;
      }

      if (e.key !== 'Tab') return;
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [requestClose]);

  /*
   * Drag guard. The scrim used to close on any click event whose target was
   * the scrim — including the click synthesised when a text selection that
   * STARTED inside the panel happened to end outside it. Selecting a draft
   * subject and releasing past the panel edge closed the dialog and lost the
   * edit. Requiring the press to have begun on the scrim fixes it.
   */
  const onScrimPointerDown = (e: React.PointerEvent) => {
    pressOrigin.current = e.target;
  };
  const onScrimClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (pressOrigin.current !== e.currentTarget) return;
    pressOrigin.current = null;
    requestClose();
  };

  return createPortal(
    <div
      className={variant === 'drawer' ? 'drawer-scrim' : 'modal-scrim'}
      role="presentation"
      onPointerDown={onScrimPointerDown}
      onClick={onScrimClick}
    >
      <div
        ref={panelRef}
        className={variant === 'drawer' ? 'drawer' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
